/* eslint-disable react-hooks/exhaustive-deps */
import { Form } from 'react-bootstrap';
import { Text } from '../../../../components/typography';
import TextWithIcon from '../../../../components/typography/TextWithIcon';
import { useWorkspace } from '../../../../providers/WorkspaceProvider';
import {
  graphTypes,
  reportAttachmentTypes as attachmentTypesConst,
  reportDateOptions,
  filterOperators,
  buildReportOptions,
} from '../../../../utilities/reports';
import {
  createFilterString,
  EXTRA_DATA_COLUMN_KEYS,
  excelHeaderLookup,
} from '@opas-mobile/report-export';
import GraphDateSelectors from '../GraphDateSelectors';
import styles from './ReportBuildMenu.module.scss';
import Icon from '../../../../components/Icon';
import { customColors } from '../../../../utilities';
import { useEffect, useMemo, useRef, useState } from 'react';
import RichTextEditor from '../../../../components/rich_text/RichTextEditor';
import { Button } from '../../../../components';
import DraggableModal from '../../../../components/modals/DraggableModal';
import Spacer from '../../../../components/typography/Spacer';
import classNames from 'classnames';
import UniversalDropdown from '../../../../components/dropdownV2/UniversalDropdown';
import { Box, TextField } from '@mui/material';
import { useLabels } from '../../../../providers/LabelProvider';
import { keys } from '../../../../utilities/translator/translation_keys';
import { useTranslation } from 'react-i18next';
import { useTemplates } from '../../../../providers/TemplateProvider';
import { useCurrentUser } from '../../../../providers/UserProvider';
import ReportBuildMenuStats from './ReportBuildMenuStats';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { allTypesOption } from '../../../../constants/strings';

// The DATA column picker's full option list: model.columns (the same
// fields the on-screen table already shows) plus the model's identifier
// columns (OPAS_ID and, where relevant, externalId/documentNumber) —
// offered as ordinary, removable, user-orderable options rather than
// forced in, per EXTRA_DATA_COLUMN_KEYS in report-export.
function allDataColumnOptions(model) {
  const fromColumns = (model?.columns || [])
    .filter((c) => c?.accessorKey)
    .map((c) => ({
      value: c.accessorKey,
      title: typeof c.header === 'string' ? c.header : c.accessorKey,
    }));
  const idKeys = EXTRA_DATA_COLUMN_KEYS[model?.value] ?? ['id'];
  const fromIds = idKeys
    .filter((key) => !fromColumns.some((c) => c.value === key))
    .map((key) => ({ value: key, title: excelHeaderLookup[key] ?? key }));
  return [...fromIds, ...fromColumns];
}

export default function ReportBuildMenu({
  onSubmit = () => {},
  setMenuState = () => {},
  menuState = {},
  open,
  onClose,
  attachment = null,
  allAttachments = [],
  onEdit = () => {},
  menuType = 'TEXT',
  setMenuType,
  setLoading = () => {},
  moveAttachment = () => {},
}) {
  const { t } = useTranslation();
  const { models } = useLabels();
  const { allWorkspaces } = useWorkspace();
  const [text, setText] = useState('');
  const isText = menuType === 'TEXT';
  const isStats = menuType === 'STATS';
  const initialTextRef = useRef('');
  const initialSpecRef = useRef(null);
  const initialDescriptionRef = useRef('');
  const { allTemplates } = useTemplates();
  const { assessmentTypes, company } = useCurrentUser();
  const { enableTemplateStats } = useFlags();
  const [selectedType, setSelectedType] = useState(allTypesOption);

  const reportAttachmentTypes = useMemo(
    () =>
      enableTemplateStats
        ? (attachmentTypesConst.stats = {
            key: 'STATS',
            type: [],
            title: 'Stats',
            icon: 'query_stats',
          }) && attachmentTypesConst
        : attachmentTypesConst,
    [enableTemplateStats],
  );
  const templateTypeOptions = useMemo(
    () => [
      allTypesOption,
      ...Object.values(assessmentTypes).filter(
        (a) => a.key !== 'OBSERVATION' && !a.internalType,
      ),
    ],
    [assessmentTypes],
  );
  const templateOptions = useMemo(() => {
    if (!selectedType || selectedType.key === 'ALL_TYPES') {
      return allTemplates.filter((t) => !t.isArchived);
    }
    return allTemplates.filter(
      (t) => !t.isArchived && t.templateType === selectedType.key,
    );
  }, [selectedType, allTemplates]);

  useEffect(() => {
    if (attachment?.id && attachment?.type) {
      initialDescriptionRef.current = attachment.description || '';
      if (attachment.type === 'TEXT') {
        setMenuType('TEXT');
        initialTextRef.current = attachment.content || '';
        setText(initialTextRef.current);
      } else if (attachment.type === 'STATS') {
        let spec = {};
        try {
          spec = JSON.parse(attachment.content || '{}');
        } catch (e) {
          spec = {};
        }
        setMenuState({ ...spec, type: 'STATS' });
        setMenuType('STATS');
      } else {
        setMenuType(attachment?.type === 'DATA' ? 'DATA' : 'GRAPH');
        let spec = {};
        try {
          spec = JSON.parse(attachment.content || '{}');
        } catch (e) {
          spec = {};
        }
        initialSpecRef.current = spec;
        setMenuState({
          ...spec,
          description: attachment.description || spec.description || '',
        });
      }
    } else {
      initialTextRef.current = '';
      initialSpecRef.current = null;
      initialDescriptionRef.current = '';
      setText('');
    }
    // Keyed on the attachment's own fields (not the object reference) so an
    // async refetch that hands back a new `attachment` object for the same
    // record - e.g. moveAttachment's setEditingAttachment({...}) - doesn't
    // re-seed state and clobber in-progress unsaved edits.
  }, [
    attachment?.id,
    attachment?.type,
    attachment?.content,
    attachment?.description,
  ]);

  const selectedGroupOptions = useMemo(() => {
    const m = menuState.model || 'actionItem';
    const allGroups = models[m]?.groups || [];
    if (!allGroups?.length) return { groups: [], selected: {} };
    const groups = allGroups;
    const subgroups = groups.filter(
      (g) => g.key !== menuState.group && g.key !== 'TRIF',
    );
    const selected = groups.find((g) => g.key === menuState.group) || groups[0];
    const subselected =
      !menuState?.subgroup || menuState.subgroup === menuState.group
        ? null
        : groups.find((g) => g.key === menuState?.subgroup);
    return { groups, selected, subselected, subgroups };
  }, [menuState]);

  const selectedColumnOptions = useMemo(() => {
    const m = menuState.model || 'actionItem';
    const allColumns = models[m]?.pivotColumns || [];
    if (allColumns?.length < 1) {
      return { columns: [], selected: null };
    }
    const columns = allColumns.filter(
      (c) =>
        !(menuState?.columns ?? [])?.map((s) => s?.value)?.includes(c.value),
    );
    const selected = menuState?.columns;
    return { columns, selected };
  }, [menuState]);

  // DATA type's own column picker — a plain accessorKey string per
  // selection (menuState.columns), not the richer pivotColumns shape above;
  // the same field name is safe to reuse since a given attachment is only
  // ever one type at a time. Presence of a non-empty selection is what
  // switches a DATA attachment from today's grouped/counted view to a flat,
  // one-row-per-record table (see the Columns/Grouped toggle below) — kept
  // as the single source of truth rather than a separate mode flag, so the
  // two can't disagree.
  const selectedDataColumnOptions = useMemo(() => {
    const m = menuState.model || 'actionItem';
    const allColumns = allDataColumnOptions(models[m]);
    const selectedKeys = new Set(menuState?.columns ?? []);
    const columns = allColumns.filter((c) => !selectedKeys.has(c.value));
    // Reflects the stored order (the user's own pick order), not
    // allColumns' order.
    const byKey = new Map(allColumns.map((c) => [c.value, c]));
    const selected = (menuState?.columns ?? [])
      .map((key) => byKey.get(key))
      .filter(Boolean);
    return { allColumns, columns, selected };
  }, [menuState, models]);
  const isDataFlatMode = menuType === 'DATA' && menuState?.columns?.length > 0;

  // "Status", "Type", etc. — one button per distinct pivotColumns group
  // (grouped by the same `field` used to build the filter/columns picker
  // elsewhere), so a user can add every option in a group at once instead
  // of hunting through the flat column list to find, e.g., every incident
  // status one at a time. They still land in the same `menuState.columns`
  // as picking them individually, so removing a few afterward works exactly
  // as it already does via the dropdown below.
  const columnGroups = useMemo(() => {
    const m = menuState.model || 'actionItem';
    const allColumns = models[m]?.pivotColumns || [];
    const seen = new Map();
    for (const c of allColumns) {
      if (!c?.field || seen.has(c.field)) continue;
      seen.set(c.field, c.fieldString ?? c.field);
    }
    return Array.from(seen, ([field, fieldString]) => ({ field, fieldString }));
  }, [menuState.model, models]);

  const addColumnGroup = (field) => {
    const m = menuState.model || 'actionItem';
    const allColumns = models[m]?.pivotColumns || [];
    const existingValues = new Set(
      (menuState?.columns ?? []).map((c) => c?.value),
    );
    const toAdd = allColumns
      .filter((c) => c.field === field && !existingValues.has(c.value))
      .map((c) => ({
        value: c?.value,
        title: c?.title,
        field: c?.field,
        fieldString: c?.fieldString ?? c?.field,
        ...(c?.field === 'computed_risk' && {
          impactField: c?.impactField,
          probabilityField: c?.probabilityField,
          min: c?.min,
          max: c?.max,
        }),
      }));
    if (!toAdd.length) return;
    setMenuState({
      ...menuState,
      columns: [...(menuState?.columns ?? []), ...toAdd],
    });
  };

  // menuState.workspaces holds a mix of shapes depending on how it got
  // there: full objects fresh off the picker's onChange, or raw ids when
  // loaded straight from a saved attachment's content (see
  // buildReportOptions), plus the synthetic 'company_shared' string either
  // way. Resolved per-entry (not by inspecting workspaces[0], which picks
  // the wrong branch whenever 'company_shared' isn't at index 0 and leaves
  // the rest of the array unhydrated, or is at index 0 and leaves a stray
  // unmatched entry for it) so every shape/order combination hydrates
  // correctly.
  const selectedWorkspaces = useMemo(() => {
    return (menuState?.workspaces ?? [])
      .map((w) => {
        if (w === 'company_shared') {
          return {
            label: 'Company Shared',
            icon: 'public',
            color: customColors.BLUE,
            value: 'null',
            title: 'Company Shared',
            id: 'company_shared',
          };
        }
        if (typeof w === 'object') return w;
        return allWorkspaces.find((wp) => wp.id === w);
      })
      .filter(Boolean);
  }, [menuState, allWorkspaces]);

  const selectedFilterOptions = useMemo(() => {
    const m = menuState.model || 'actionItem';
    const fields = models[m]?.fields ?? [];
    const selectedFilter =
      menuState?.filters?.find((fil) => !fil?.default) ?? null;
    const selectedField =
      fields.find((f) => f.value === selectedFilter?.field) ?? null;
    const values = selectedField ? models[m]?.values[selectedField?.value] : [];
    const selectedOperator =
      selectedFilter?.operator === 'notIn'
        ? filterOperators[1]
        : filterOperators[0];
    const selectedValues = [];
    for (const v of selectedFilter?.value ?? []) {
      const valueToAdd = values?.find((val) => val.value === v);
      if (valueToAdd) {
        selectedValues.push(valueToAdd);
      }
    }
    // `values` (above) intentionally includes archived templates, so an
    // already-saved filter referencing one still resolves to its real name
    // instead of rendering blank — see reports.js's templateId comment.
    // Picking a *new* filter value should still only offer active ones.
    const pickableValues =
      selectedField?.value === 'templateId'
        ? values?.filter((v) => !v?.isArchived)
        : values;
    // Date fields (isDate) use gte/lte/between instead of in/notIn — derive
    // the current from/to strings back out of whichever shape is stored so
    // the date inputs below stay controlled/pre-filled on an edit.
    const selectedMinDate =
      selectedFilter?.operator === 'gte' ||
      selectedFilter?.operator === 'between'
        ? selectedFilter?.value?.[0] ?? ''
        : '';
    const selectedMaxDate =
      selectedFilter?.operator === 'lte'
        ? selectedFilter?.value?.[0] ?? ''
        : selectedFilter?.operator === 'between'
        ? selectedFilter?.value?.[1] ?? ''
        : '';
    return {
      fields,
      selectedField,
      selectedOperator,
      selectedValues,
      values,
      pickableValues,
      selectedMinDate,
      selectedMaxDate,
    };
  }, [menuState]);

  // Date-range filters don't use the in/notIn operator picker — from/to are
  // combined into gte (only min), lte (only max), or between (both), mirroring
  // the same operator selection IncidentTable.jsx's CustomDateRangeFilter
  // produces for the main incidents list.
  const updateDateRangeFilter = (min, max) => {
    const defaultFilters = models[menuState.model ?? 'actionItem']?.filters;
    setMenuState((prev) => {
      const filterToEdit = prev.filters.find((fil) => !fil?.default);
      let operator = 'in';
      let value = [];
      if (min && max) {
        operator = 'between';
        value = [min, max];
      } else if (min) {
        operator = 'gte';
        value = [min];
      } else if (max) {
        operator = 'lte';
        value = [max];
      }
      return {
        ...prev,
        filters: [
          ...defaultFilters,
          { field: filterToEdit?.field, operator, value },
        ],
      };
    });
  };

  const hasFilters = useMemo(() => {
    if (menuType === 'STATS') return false;
    const model = models[menuState?.model];
    return !!model?.filters;
  }, [menuState, models]);

  useEffect(() => {
    if (menuState.group === 'TRIF') {
      setMenuState({
        ...menuState,
        type: 'LINE',
        daysBack: '365',
      });
    }
  });
  const { template } = menuState || menuState?.template || {};

  useEffect(
    () => setMenuState({ ...menuState, template: null }),
    [selectedType],
  );

  return (
    <DraggableModal
      closeOnOutsideClick={false}
      open={open}
      title={!!attachment?.id ? 'Edit Content' : 'Add Report Content'}
      width={menuType === 'STATS' && !!template?.id ? '60rem' : '25rem'}
      height="95vh"
      onClose={() => {
        onClose();
      }}
    >
      <div className={styles.split}>
        <div className={styles.root}>
          <div className={styles.container}>
            <div className={styles.tabs}>
              {Object.values(reportAttachmentTypes).map(
                ({ key, title, icon }) => {
                  const isSelected = menuType === key;
                  return (
                    <div
                      key={key}
                      className={isSelected ? styles.selectedTab : styles.tab}
                    >
                      <TextWithIcon
                        weight="semiBold"
                        hover
                        icon={icon}
                        color={
                          isSelected
                            ? customColors.BLUE
                            : customColors.SECONDARY_LIGHT
                        }
                        onClick={() => {
                          setMenuType(key);
                          setMenuState({
                            ...menuState,
                            type: key === 'GRAPH' ? 'PIE' : key,
                            // Column selection is the new default for DATA
                            // (easier to understand than grouped counts) —
                            // pre-select every column unless one's already
                            // been picked for this attachment.
                            ...(key === 'DATA' && !menuState?.columns?.length
                              ? {
                                  columns: allDataColumnOptions(
                                    models[menuState.model || 'actionItem'],
                                  ).map((c) => c.value),
                                }
                              : {}),
                          });
                        }}
                        iconProps={{
                          fontSize: '1rem',
                          color: isSelected
                            ? customColors.BLUE
                            : customColors.SECONDARY_LIGHT,
                        }}
                      >{`${title}`}</TextWithIcon>
                    </div>
                  );
                },
              )}
            </div>
            <Spacer />
            {menuType === 'TEXT' ? (
              <div className={styles.richTextContainer}>
                <div className={styles.richText}>
                  <RichTextEditor
                    onChange={(value) => {
                      setText(value);
                    }}
                    value={text}
                    id={1}
                    className={styles.rightSide}
                    height={'2rem'}
                  />
                </div>
              </div>
            ) : (
              <div className={styles.section}>
                {menuType === 'STATS' ? (
                  <div className={styles.fullWidth}>
                    <div>
                      <Text size="sm-md" noMargin weight="semiBold">
                        {t(keys.common.TYPE)}
                      </Text>
                      <UniversalDropdown
                        selected={selectedType}
                        options={templateTypeOptions}
                        onChange={(value) => {
                          setSelectedType(value);
                        }}
                        type="templateType"
                      />
                      <Spacer />
                      <Text size="sm-md" noMargin weight="semiBold">
                        {t(keys.common.TEMPLATE)}
                      </Text>
                      <UniversalDropdown
                        selected={menuState.template}
                        options={templateOptions}
                        onChange={(value) => {
                          const { categories, id, title, templateType } = value;
                          const template = {
                            categories,
                            id,
                            title,
                            templateType,
                          };
                          setMenuState({
                            ...menuState,
                            template,
                            categories,
                            title: value?.title,
                          });
                        }}
                        type="template"
                        placeholder={'Select Template...'}
                      />
                    </div>
                  </div>
                ) : (
                  <div>
                    <Text size="sm-md" weight="semiBold" noMargin>
                      Data Type
                    </Text>
                    <div className={styles.buttons}>
                      {Object.entries(models)?.map(([k, v]) => {
                        if (!v.show) return null;
                        const isSelected = menuState.model === k;

                        return (
                          <div
                            className={classNames([
                              styles.button,
                              isSelected && styles.selected,
                            ])}
                            onClick={() => {
                              setMenuState({
                                ...menuState,
                                filters: v?.filters ?? null,
                                model: k,
                                refModel: v.refModel ?? k,
                                group: v?.group,
                                columns: null,
                              });
                            }}
                            role="button"
                            key={k}
                          >
                            <Icon
                              style={{
                                fontSize: '1.2rem',
                                color: isSelected
                                  ? customColors.BLUE
                                  : `${customColors.SECONDARY_LIGHT} !important`,
                              }}
                            >
                              {v.icon}
                            </Icon>
                            <Text
                              color={
                                isSelected ? 'accentPrimary' : 'secondaryLight'
                              }
                              noMargin
                              textAlign="center"
                              weight={isSelected ? 'semiBold' : 'regular'}
                            >
                              {v.label}
                            </Text>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
                {menuState?.group !== 'TRIF' && menuType !== 'TEXT' && (
                  <div>
                    <Text size="sm-md" weight="semiBold" noMargin>
                      Timeframe (Days)
                    </Text>
                    <GraphDateSelectors
                      key={attachment?.id ?? 'new'}
                      buttonOptions={reportDateOptions}
                      selected={menuState?.daysBack}
                      initialFrom={menuState?.timeRange?.from}
                      initialTo={menuState?.timeRange?.to}
                      buttonView
                      showTitle={false}
                      setDates={({ start, end }) => {
                        setMenuState((prev) => ({
                          ...prev,
                          timeRange: { from: start, to: end },
                        }));
                      }}
                      setDateType={(value) => {
                        setMenuState((prev) => ({
                          ...prev,
                          daysBack: String(value || '30'),
                        }));
                      }}
                    />
                    {menuState?.daysBack === 'from' && (
                      <Text size="sm" color="secondary" noMargin>
                        Extends automatically from the chosen date to today,
                        each time this report regenerates.
                      </Text>
                    )}
                  </div>
                )}
                {menuType === 'GRAPH' && (
                  <div>
                    <Text size="sm-md" weight="semiBold" noMargin>
                      Chart
                    </Text>
                    <div className={styles.graphs}>
                      {graphTypes
                        .filter(({ key }) =>
                          selectedColumnOptions?.columns?.length > 0
                            ? key
                            : key !== 'PIVOT',
                        )
                        .map((t) => {
                          const isSelected = menuState?.type === t.key;
                          return (
                            <div
                              key={t.key}
                              className={classNames([
                                styles.buttonXs,
                                isSelected && styles.selected,
                              ])}
                              onClick={() => {
                                setMenuState({ ...menuState, type: t.key });
                              }}
                            >
                              <Icon
                                style={{
                                  color: isSelected
                                    ? customColors.BLUE
                                    : 'black',
                                  ...t.iconProps,
                                }}
                                noMargin
                              >
                                {t.icon}
                              </Icon>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
                {menuType === 'GRAPH' && (
                  <div>
                    <Text weight="semiBold" size="sm-md" noMargin>
                      Target (optional)
                    </Text>
                    <Form.Control
                      type="number"
                      placeholder="No target set"
                      value={menuState?.target ?? ''}
                      onChange={(e) =>
                        setMenuState({
                          ...menuState,
                          target: e.target.value,
                        })
                      }
                    />
                  </div>
                )}

                <div className={styles.content}>
                  <div>
                    <Text weight="semiBold" size="sm-md" noMargin>
                      Workspaces
                    </Text>
                    <UniversalDropdown
                      isClearable
                      closeMenuOnSelect={false}
                      className={styles.fullWidth}
                      isMulti
                      type="workspaces"
                      options={allWorkspaces}
                      onChange={(workspaces) => {
                        setMenuState({
                          ...menuState,
                          workspaces: workspaces?.length
                            ? workspaces
                            : undefined,
                        });
                      }}
                      selected={selectedWorkspaces || []}
                    />
                  </div>
                  {menuType === 'DATA' && (
                    <div>
                      <Text weight="semiBold" size="sm-md" noMargin>
                        Display
                      </Text>
                      {/* Columns and Grouped are mutually exclusive views of
                          the same data, not stacking options — a saved
                          attachment is one or the other, never both, so this
                          is a toggle, not two independent settings. */}
                      <div className={styles.columnGroups}>
                        <Button
                          value="Columns"
                          size="sm"
                          outlined={!isDataFlatMode}
                          variant={isDataFlatMode ? 'primary' : 'secondary'}
                          onClick={() => {
                            if (isDataFlatMode) return;
                            const m = menuState.model || 'actionItem';
                            setMenuState({
                              ...menuState,
                              columns: allDataColumnOptions(models[m]).map(
                                (c) => c.value,
                              ),
                            });
                          }}
                        />
                        <Button
                          value="Grouped"
                          size="sm"
                          outlined={isDataFlatMode}
                          variant={!isDataFlatMode ? 'primary' : 'secondary'}
                          onClick={() => {
                            if (!isDataFlatMode) return;
                            setMenuState({ ...menuState, columns: null });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {menuType !== 'STATS' && !isDataFlatMode && (
                    <div>
                      <Text weight="semiBold" size="sm-md" noMargin>
                        Group
                      </Text>
                      <div className={styles.section}>
                        <UniversalDropdown
                          selected={selectedGroupOptions.selected}
                          options={selectedGroupOptions.groups}
                          onChange={(value) => {
                            const groupKey = value?.value ?? value?.key;
                            setMenuState({
                              ...menuState,
                              group: groupKey,
                              ...(groupKey === 'riskLevel' && {
                                riskRanges: company?.riskMatrix?.levels,
                              }),
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {menuType === 'DATA' && isDataFlatMode && (
                    <div>
                      <Text weight="semiBold" size="sm-md" noMargin>
                        Columns
                      </Text>
                      <div className={styles.section}>
                        <UniversalDropdown
                          selected={selectedDataColumnOptions.selected}
                          options={selectedDataColumnOptions.columns}
                          isMulti
                          isClearable
                          onChange={(value) => {
                            setMenuState({
                              ...menuState,
                              columns: value?.map((v) => v?.value) ?? [],
                            });
                          }}
                          // A full clear would otherwise drop
                          // columns?.length to 0 and flip straight back to
                          // Grouped mode — land on just 'id' instead, so
                          // Columns mode stays active and the user can
                          // rebuild their pick from there (removing 'id'
                          // itself afterward is still just a normal
                          // deselect, going through onChange as usual).
                          onClear={() =>
                            setMenuState({ ...menuState, columns: ['id'] })
                          }
                        />
                      </div>
                    </div>
                  )}
                  {menuType === 'DATA' && !isDataFlatMode && (
                    <div>
                      <Text weight="semiBold" size="sm-md" noMargin>
                        Subgroup
                      </Text>
                      <div className={styles.section}>
                        <UniversalDropdown
                          selected={selectedGroupOptions?.subselected || null}
                          options={selectedGroupOptions.subgroups}
                          onChange={(value) => {
                            setMenuState({
                              ...menuState,
                              subgroup: value?.value ?? value?.key,
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {menuState?.type === 'PIVOT' && (
                    <div>
                      <Text weight="semiBold" size="sm-md" noMargin>
                        Columns
                      </Text>
                      {columnGroups.length > 0 && (
                        <div className={styles.columnGroups}>
                          {columnGroups.map(({ field, fieldString }) => (
                            <Button
                              key={field}
                              value={t(fieldString) || fieldString}
                              size="sm"
                              outlined
                              variant="secondary"
                              onClick={() => addColumnGroup(field)}
                            />
                          ))}
                        </div>
                      )}
                      <div className={styles.section}>
                        <UniversalDropdown
                          selected={selectedColumnOptions?.selected || null}
                          options={selectedColumnOptions.columns}
                          isMulti
                          isClearable
                          onChange={(value) => {
                            setMenuState({
                              ...menuState,
                              columns: value?.map((v) => ({
                                value: v?.value,
                                title: v?.title,
                                field: v?.field,
                                fieldString: v?.fieldString ?? v?.field,
                                ...(v?.field === 'computed_risk' && {
                                  impactField: v?.impactField,
                                  probabilityField: v?.probabilityField,
                                  min: v?.min,
                                  max: v?.max,
                                }),
                              })),
                            });
                          }}
                        />
                      </div>
                    </div>
                  )}
                  {hasFilters && (
                    <div>
                      <div className={styles.sbetween}>
                        <Text weight="semiBold" size="sm-md" noMargin>
                          Filters
                        </Text>
                        <Text
                          textAlign="right"
                          color="accentPrimary"
                          size="sm"
                          weight="semiBold"
                          hover
                          noMargin
                          onClick={() => {
                            const defaultFilters =
                              models[menuState.model ?? 'actionItem']?.filters;
                            setMenuState((prev) => {
                              return {
                                ...prev,
                                filters: [...defaultFilters],
                              };
                            });
                          }}
                        >
                          {t(keys.action.CLEAR)}
                        </Text>
                      </div>
                      <div
                        className={classNames(
                          styles.section,
                          styles.flex,
                          styles.alignBaseline,
                        )}
                      >
                        <div className={styles.fieldName}>
                          <UniversalDropdown
                            isClearable
                            placeholder={'Select Property'}
                            selected={selectedFilterOptions?.selectedField}
                            options={selectedFilterOptions?.fields}
                            onChange={({ value }) => {
                              const defaultFilters =
                                models[menuState.model ?? 'actionItem']
                                  ?.filters;
                              const isSubfilter = !!models[
                                menuState.model ?? 'actionItem'
                              ]?.fields?.find((field) => field.value === value)
                                ?.subfilter;
                              setMenuState((prev) => ({
                                ...prev,
                                filters: [
                                  ...defaultFilters,
                                  {
                                    field: value,
                                    operator: 'in',
                                    value: [],
                                    subfilter: isSubfilter,
                                  },
                                ],
                              }));
                            }}
                          />
                        </div>
                        {!selectedFilterOptions?.selectedField?.isDate && (
                          <div className={styles.operator}>
                            <UniversalDropdown
                              hideArrow
                              selected={selectedFilterOptions?.selectedOperator}
                              options={filterOperators}
                              onChange={({ value }) => {
                                const defaultFilters =
                                  models[menuState.model ?? 'actionItem']
                                    ?.filters;

                                setMenuState((prev) => {
                                  const filterToEdit = prev.filters.find(
                                    (fil) => !fil?.default,
                                  );
                                  return {
                                    ...prev,
                                    filters: [
                                      ...defaultFilters,
                                      {
                                        ...filterToEdit,
                                        operator: value,
                                      },
                                    ],
                                  };
                                });
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <div className={styles.section}>
                        {selectedFilterOptions?.selectedField?.isDate ? (
                          <Box display="flex" gap="0.5rem">
                            <TextField
                              type="date"
                              size="small"
                              label="From"
                              InputLabelProps={{ shrink: true }}
                              value={selectedFilterOptions?.selectedMinDate}
                              onChange={(e) =>
                                updateDateRangeFilter(
                                  e.target.value,
                                  selectedFilterOptions?.selectedMaxDate,
                                )
                              }
                            />
                            <TextField
                              type="date"
                              size="small"
                              label="To"
                              InputLabelProps={{ shrink: true }}
                              value={selectedFilterOptions?.selectedMaxDate}
                              onChange={(e) =>
                                updateDateRangeFilter(
                                  selectedFilterOptions?.selectedMinDate,
                                  e.target.value,
                                )
                              }
                            />
                          </Box>
                        ) : (
                          <UniversalDropdown
                            isClearable
                            selected={selectedFilterOptions?.selectedValues}
                            options={selectedFilterOptions?.pickableValues}
                            type={
                              selectedFilterOptions?.selectedField?.value ===
                              'templateId'
                                ? 'template'
                                : [
                                    'creatorId',
                                    'assigneeId',
                                    'participants',
                                  ].includes(
                                    selectedFilterOptions?.selectedField?.value,
                                  )
                                ? 'user'
                                : undefined
                            }
                            placeholder={'Select Value(s)'}
                            isMulti
                            onChange={(value) => {
                              const defaultFilters =
                                models[menuState.model ?? 'actionItem']
                                  ?.filters;

                              setMenuState((prev) => {
                                const filterToEdit = prev.filters.find(
                                  (fil) => !fil?.default,
                                );

                                const isSubfilter = !!models[
                                  menuState.model ?? 'actionItem'
                                ]?.fields?.find(
                                  (field) => field.value === filterToEdit.field,
                                )?.subfilter;

                                return {
                                  ...prev,
                                  filters: [
                                    ...defaultFilters,
                                    {
                                      field: filterToEdit?.field,
                                      operator: filterToEdit?.operator,
                                      value: value.map(({ value }) => value),
                                      subfilter: isSubfilter
                                        ? isSubfilter
                                        : undefined,
                                    },
                                  ],
                                };
                              });
                            }}
                          />
                        )}
                      </div>
                    </div>
                  )}
                  <div>
                    <Text weight="semiBold" size="sm-md" noMargin>
                      Title
                    </Text>
                    <Form.Control
                      value={menuState.title}
                      onChange={(e) =>
                        setMenuState({
                          ...menuState,
                          title: e.target.value,
                        })
                      }
                    />
                  </div>
                  <div>
                    <Text weight="semiBold" size="sm-md" noMargin>
                      Description
                    </Text>
                    <Form.Control
                      rows={2}
                      value={menuState.description}
                      as="textarea"
                      onChange={(e) =>
                        setMenuState({
                          ...menuState,
                          description: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
              </div>
            )}
            {menuType === 'STATS' && (
              <div>
                <Spacer />
                <Text noMargin color="secondary" textAlign="center" size="sm">
                  {`**${t(keys.report.DISCLAIMER)}`}
                </Text>
              </div>
            )}

            {attachment?.id && (
              <div
                className={classNames(
                  styles.organizationButtons,
                  styles.marginTopBottom,
                )}
              >
                {Math.min(...allAttachments.map((obj) => obj.sortIndex)) !==
                  attachment?.sortIndex && (
                  <Button
                    size="sm"
                    outlined
                    icon="keyboard_double_arrow_up"
                    value={'Move Up'}
                    onClick={() => {
                      const indexes = [...allAttachments]
                        .sort((a, b) => a.sortIndex - b.sortIndex)
                        .map((a, i) => ({
                          ...a,
                          index: i,
                        }));

                      const currentIndex = indexes.find(
                        (a) => a.sortIndex === attachment.sortIndex,
                      )?.index;

                      const nextIndex = indexes[currentIndex - 1]?.index;

                      const newSortIndex =
                        indexes[nextIndex]?.sortIndex ??
                        attachment.sortIndex - 10;

                      moveAttachment({
                        id: attachment?.id,
                        sortIndex: newSortIndex,
                      });
                    }}
                  />
                )}
                {Math.max(...allAttachments.map((obj) => obj.sortIndex)) !==
                  attachment?.sortIndex && (
                  <Button
                    size="sm"
                    outlined
                    icon="keyboard_double_arrow_down"
                    value={'Move Down'}
                    onClick={() => {
                      const indexes = [...allAttachments]
                        .sort((a, b) => a.sortIndex - b.sortIndex)
                        .map((a, i) => ({
                          ...a,
                          index: i,
                        }));
                      const currentIndex = indexes.find(
                        (a) => a.sortIndex === attachment.sortIndex,
                      )?.index;

                      const nextIndex = indexes[currentIndex + 1]?.index;

                      const newSortIndex =
                        indexes[nextIndex]?.sortIndex ??
                        attachment.sortIndex + 10;

                      moveAttachment({
                        id: attachment?.id,
                        sortIndex: newSortIndex,
                      });
                    }}
                  />
                )}
              </div>
            )}
            <br />
            <br />
            <br />
            <br />
            <div className={styles.footer}>
              <Button
                align="right"
                disabled={
                  (!text && isText) ||
                  (isStats && !menuState?.questions?.length)
                }
                value={!attachment?.id ? 'Add to Report' : 'Save Changes'}
                icon="add"
                onClick={async () => {
                  setLoading(true);
                  let savePromise;
                  if (menuType === 'STATS') {
                    const {
                      questions,
                      daysBack = '30',
                      description,
                      title,
                      from,
                      template,
                      to,
                      total,
                      workspaces,
                      timeRange,
                      graphType,
                    } = menuState || {};
                    const vars = {
                      questions,
                      daysBack,
                      description,
                      title,
                      from,
                      template,
                      to,
                      total,
                      workspaces,
                      timeRange,
                      graphType: graphType || 'BAR_HORIZONTAL',
                      type: 'STATS',
                    };
                    const content = JSON.stringify(vars);

                    if (!!attachment?.id) {
                      savePromise = onEdit({
                        id: attachment.id,
                        content,
                      });
                    } else {
                      savePromise = onSubmit({
                        content,
                        type: 'STATS',
                      });
                    }
                  } else {
                    const santizedFilters = (
                      menuState?.filters?.filter((f) => f?.value?.length > 0) ??
                      []
                    ).map((f) => {
                      // Resolved once here, while `models` still has this
                      // company's full custom type/name config, and stored
                      // on the filter itself — so an emailed report can show
                      // the identical "Field = Value" text without the
                      // backend needing any of that config. See
                      // buildChartTitle, which prefers this stored label
                      // over recomputing it. Only the primary (non-default)
                      // filter has a rendered chip/caption at all, so that's
                      // the only one worth labelling.
                      if (f?.default) return f;
                      const m = menuState.model || 'actionItem';
                      return {
                        ...f,
                        filterLabel: createFilterString(f, models[m], t),
                      };
                    });
                    const queryOptions = buildReportOptions({
                      ...menuState,
                      filters: santizedFilters,
                    });
                    const content = isText
                      ? text
                      : JSON.stringify(queryOptions);
                    if (!!attachment?.id) {
                      savePromise = onEdit({
                        id: attachment.id,
                        content,
                        type: isText ? 'TEXT' : menuState.type,
                        description: menuState.description,
                      });
                    } else {
                      savePromise = onSubmit({
                        content,
                        type: isText ? 'TEXT' : menuState.type,
                        description: menuState.description,
                      });
                    }
                  }
                  setText('');
                  setMenuType('TEXT');
                  // Wait for the save mutation (and its refetch) to finish
                  // before closing — closing early raced its own refetch
                  // against the save, sometimes leaving stale data rendered
                  // until a manual page reload.
                  await savePromise;
                  onClose();
                  setLoading(false);
                }}
              />
            </div>
          </div>
        </div>
        {menuType === 'STATS' && !!template?.id ? (
          <div style={{ width: '35rem' }}>
            <ReportBuildMenuStats
              template={menuState.template}
              options={menuState}
              setMenuState={setMenuState}
              isEditing={true}
            />
          </div>
        ) : null}
      </div>
    </DraggableModal>
  );
}
